/**
 * Копирование HTML-таблицы: браузер кладёт TAB между ячейками,
 * а редактор заменяет TAB на 4 пробела. Для ЕГЭ-2 нужны ячейки через один пробел.
 */

function nodeToElement(node) {
  if (!node) return null;
  return node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
}

function normalizeCellText(cell) {
  const raw = cell.innerText ?? cell.textContent ?? '';
  return String(raw).replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

function rangeIntersectsNode(range, node) {
  try {
    return range.intersectsNode(node);
  } catch {
    return false;
  }
}

/** Выделение пересекает узел (или лежит внутри него). */
export function selectionIntersectsNode(selection, node) {
  if (!selection || !node || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  if (node.contains?.(range.commonAncestorContainer)) return true;
  return rangeIntersectsNode(range, node);
}

function findTableForRange(range) {
  const startEl = nodeToElement(range.startContainer);
  const endEl = nodeToElement(range.endContainer);
  const fromStart = startEl?.closest?.('table');
  if (fromStart && (!endEl || fromStart.contains(endEl) || endEl.closest?.('table') === fromStart)) {
    return fromStart;
  }
  const fromEnd = endEl?.closest?.('table');
  if (fromEnd) return fromEnd;

  const root = nodeToElement(range.commonAncestorContainer);
  if (!root?.querySelectorAll) return null;
  for (const table of root.querySelectorAll('table')) {
    if (rangeIntersectsNode(range, table)) return table;
  }
  return null;
}

/**
 * Текст для буфера: ячейки через один пробел.
 * Пустая ячейка остаётся пустым полем («1  1»).
 * Если таблицу не разобрали, но в выделении есть TAB (типичный table-copy) — заменяем TAB на пробел.
 */
export function serializeSelectedTableAsSpaces(selection = typeof window !== 'undefined' ? window.getSelection() : null) {
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;

  const range = selection.getRangeAt(0);
  const table = findTableForRange(range);

  if (table) {
    const lines = [];
    for (const tr of table.querySelectorAll('tr')) {
      const cells = [...tr.querySelectorAll(':scope > th, :scope > td')];
      if (cells.length === 0) continue;

      const picked = [];
      let any = false;
      for (const cell of cells) {
        if (!rangeIntersectsNode(range, cell)) continue;
        any = true;
        picked.push(normalizeCellText(cell));
      }
      // Строка таблицы задета, но intersectsNode по ячейкам пуст (край выделения) —
      // берём все ячейки строки, если задета сама tr.
      if (!any && rangeIntersectsNode(range, tr)) {
        for (const cell of cells) picked.push(normalizeCellText(cell));
        any = picked.length > 0;
      }
      if (any) lines.push(picked.join(' '));
    }
    if (lines.length > 0) return lines.join('\n');
  }

  // Браузер уже сериализовал таблицу с TAB — этого достаточно для вставки в редактор.
  const raw = selection.toString();
  if (raw.includes('\t')) return raw.replace(/\t/g, ' ');
  return null;
}

/** Задачи ЕГЭ-2: source_name «ЕГЭ-2» или slug вида ege2-…. */
export function shouldCopyTablesAsSpaces({ source_name, slug } = {}) {
  if (String(source_name || '').trim() === 'ЕГЭ-2') return true;
  return String(slug || '').startsWith('ege2-');
}
